import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  RISK_LEVELS, INTERACTION_TYPES, CONFIDENCE_LEVELS,
} from "./catalogMeta";

/**
 * One generic form for all 4 catalog collections. Renders only the fields
 * that exist on the current `slug`. Multi-value fields (brand_names,
 * aliases, key_ingredient_ids) accept comma-separated strings.
 */
export default function CatalogForm({ slug, value, onChange, mediaPickers }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setList = (k, csv) =>
    set(k, csv.split(",").map((s) => s.trim()).filter(Boolean));

  return (
    <div className="space-y-4" data-testid={`catalog-form-${slug}`}>
      <Field label="ID (UUID or slug)" required>
        <Input
          value={value.id || ""}
          onChange={(e) => set("id", e.target.value)}
          placeholder="e.g. 11111111-aaaa-4aaa-aaaa-000000000099"
          data-testid="catalog-form-id"
        />
      </Field>

      {slug === "medications" && (
        <>
          <Field label="Generic name" required>
            <Input value={value.generic_name || ""}
              onChange={(e) => set("generic_name", e.target.value)} />
          </Field>
          <Field label="Brand names (comma-separated)">
            <Input value={(value.brand_names || []).join(", ")}
              onChange={(e) => setList("brand_names", e.target.value)} />
          </Field>
          <Field label="Category" required>
            <Input value={value.category || ""}
              onChange={(e) => set("category", e.target.value)} />
          </Field>
          <Field label="Description" required>
            <Textarea rows={3} value={value.description || ""}
              onChange={(e) => set("description", e.target.value)} />
          </Field>
          <Row>
            <Field label="FDA label URL">
              <Input value={value.fda_label_url || ""}
                onChange={(e) => set("fda_label_url", e.target.value)} />
            </Field>
            <Field label="Typical duration">
              <Input value={value.typical_duration || ""}
                onChange={(e) => set("typical_duration", e.target.value)} />
            </Field>
          </Row>
          <Field label="US prevalence">
            <Input value={value.us_prevalence || ""}
              onChange={(e) => set("us_prevalence", e.target.value)} />
          </Field>
          <Toggle label="Has course timeline (shows in Course Tracker)"
            checked={!!value.has_course}
            onChange={(v) => set("has_course", v)} />
        </>
      )}

      {slug === "ingredients" && (
        <>
          <Field label="Name (must be unique)" required>
            <Input value={value.name || ""}
              onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="INCI name">
            <Input value={value.inci_name || ""}
              onChange={(e) => set("inci_name", e.target.value)} />
          </Field>
          <Field label="Aliases (comma-separated)">
            <Input value={(value.aliases || []).join(", ")}
              onChange={(e) => setList("aliases", e.target.value)}
              placeholder="Vitamin A, Retinaldehyde, …" />
          </Field>
          <Field label="Category" required>
            <Input value={value.category || ""}
              onChange={(e) => set("category", e.target.value)} />
          </Field>
          <Field label="Description">
            <Textarea rows={2} value={value.description || ""}
              onChange={(e) => set("description", e.target.value)} />
          </Field>
          <Field label="Commonly found in">
            <Input value={value.commonly_found_in || ""}
              onChange={(e) => set("commonly_found_in", e.target.value)} />
          </Field>
        </>
      )}

      {slug === "interactions" && (
        <>
          <Row>
            <Field label="Medication" required>
              <Picker options={mediaPickers.medications}
                getId={(o) => o.id}
                getLabel={(o) => `${o.generic_name}${(o.brand_names || [])[0] ? ` (${o.brand_names[0]})` : ""} (…${o.id.slice(-6)})`}
                value={value.medication_id}
                onChange={(v) => set("medication_id", v)}
                testId="picker-medication" />
            </Field>
            <Field label="Ingredient" required>
              <Picker options={mediaPickers.ingredients}
                getId={(o) => o.id}
                getLabel={(o) => `${o.name} (…${o.id.slice(-6)})`}
                value={value.ingredient_id}
                onChange={(v) => set("ingredient_id", v)}
                testId="picker-ingredient" />
            </Field>
          </Row>
          <Row>
            <Field label="Risk level" required>
              <Select value={value.risk_level} options={RISK_LEVELS}
                onChange={(v) => set("risk_level", v)} />
            </Field>
            <Field label="Interaction type" required>
              <Select value={value.interaction_type} options={INTERACTION_TYPES}
                onChange={(v) => set("interaction_type", v)} />
            </Field>
            <Field label="Confidence" required>
              <Select value={value.confidence_level} options={CONFIDENCE_LEVELS}
                onChange={(v) => set("confidence_level", v)} />
            </Field>
          </Row>
          <Field label="What happens (scientific)" required>
            <Textarea rows={3} value={value.what_happens_scientific || ""}
              onChange={(e) => set("what_happens_scientific", e.target.value)} />
          </Field>
          <Field label="What happens (plain language)" required>
            <Textarea rows={3} value={value.what_happens_plain || ""}
              onChange={(e) => set("what_happens_plain", e.target.value)} />
          </Field>
          <Field label="Recommendation" required>
            <Textarea rows={2} value={value.recommendation || ""}
              onChange={(e) => set("recommendation", e.target.value)} />
          </Field>
          <Row>
            <Field label="Primary source" required>
              <Input value={value.source_primary || ""}
                onChange={(e) => set("source_primary", e.target.value)} />
            </Field>
            <Field label="Primary source URL">
              <Input value={value.source_primary_url || ""}
                onChange={(e) => set("source_primary_url", e.target.value)} />
            </Field>
          </Row>
          <Field label="Last reviewed (YYYY-MM-DD)" required>
            <Input value={value.last_reviewed || ""}
              onChange={(e) => set("last_reviewed", e.target.value)} />
          </Field>
          <Row>
            <Toggle label="Verified" checked={!!value.verified}
              onChange={(v) => set("verified", v)} />
            <Toggle label="Show to users" checked={!!value.show_to_users}
              onChange={(v) => set("show_to_users", v)} />
            <Toggle label="Active" checked={value.active !== false}
              onChange={(v) => set("active", v)} />
          </Row>
        </>
      )}

      {slug === "products" && (
        <>
          <Row>
            <Field label="Brand" required>
              <Input value={value.brand || ""}
                onChange={(e) => set("brand", e.target.value)} />
            </Field>
            <Field label="Name" required>
              <Input value={value.name || ""}
                onChange={(e) => set("name", e.target.value)} />
            </Field>
          </Row>
          <Field label="Category" required>
            <Input value={value.category || ""}
              onChange={(e) => set("category", e.target.value)} />
          </Field>
          <Field label="Key ingredient IDs (comma-separated)">
            <Input
              value={(value.key_ingredient_ids || []).join(", ")}
              onChange={(e) => setList("key_ingredient_ids", e.target.value)}
              placeholder="paste ingredient UUIDs" />
            <p className="mt-1 text-[11px] text-stone-500">
              Available: {(mediaPickers.ingredients || []).slice(0, 3)
                .map((i) => `${i.name}=${i.id.slice(0, 8)}`).join("  ·  ")}
            </p>
          </Field>
          <Field label="Full ingredient list (raw)">
            <Textarea rows={3} value={value.all_ingredients_raw || ""}
              onChange={(e) => set("all_ingredients_raw", e.target.value)} />
          </Field>
          <Row>
            <Field label="Barcode UPC">
              <Input value={value.barcode_upc || ""}
                onChange={(e) => set("barcode_upc", e.target.value)} />
            </Field>
            <Field label="Barcode EAN">
              <Input value={value.barcode_ean || ""}
                onChange={(e) => set("barcode_ean", e.target.value)} />
            </Field>
          </Row>
          <Row>
            <Toggle label="Safe alternative" checked={!!value.is_safe_alternative}
              onChange={(v) => set("is_safe_alternative", v)} />
            <Toggle label="Verified" checked={!!value.verified}
              onChange={(v) => set("verified", v)} />
            <Toggle label="Active" checked={value.active !== false}
              onChange={(v) => set("active", v)} />
          </Row>
        </>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-stone-600">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{children}</div>;
}

function Select({ value, options, onChange }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm"
    >
      <option value="" disabled>Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Picker({ options, getId, getLabel, value, onChange, testId }) {
  return (
    <select
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm"
    >
      <option value="" disabled>Select…</option>
      {(options || []).map((o) => (
        <option key={getId(o)} value={getId(o)}>{getLabel(o)}</option>
      ))}
    </select>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-stone-700">
      <Switch checked={checked} onCheckedChange={onChange} />
      {label}
    </label>
  );
}

// Re-export for parent use (avoids unused warning)
export { useState };
